import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import "./styles/Menu.css";

const MENU_API = "https://okna.co.tz/mrms/Menu.php";
const UPLOAD_PATH = "https://okna.co.tz/mrms/uploads/";
const ITEMS_PER_PAGE = 5;

export default function Menu() {
    const token = localStorage.getItem("token");
    const [menuItems, setMenuItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);

    const [formData, setFormData] = useState({
        group_button: "",
        item_name: "",
        price_tzs: "",
        description: "",
        item_picture: null
    });

    useEffect(() => { 
        if (!token) {
            Swal.fire("Session expired", "Please login again", "warning");
            return;
        }
        loadMenu(); 
    }, [token]);

    const loadMenu = async () => {
        try {
            const res = await fetch(`${MENU_API}?token=${token}`, {
                headers: { 
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });
            
            if (res.status === 401) {
                Swal.fire("Session Expired", "Please login again", "warning");
                localStorage.removeItem("token");
                localStorage.removeItem("staff");
                window.location.href = "/";
                return;
            }
            
            const data = await res.json();
            if (Array.isArray(data)) {
                const sorted = [...data].sort((a, b) => b.id - a.id);
                setMenuItems(sorted);
            } else {
                Swal.fire("Error", data.error || "Failed to load menu", "error");
            }
        } catch (error) {
            console.error("Load error:", error);
            Swal.fire("Error", "Server not reachable", "error");
        }
    };

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        setFormData(prev => ({ ...prev, [name]: files ? files[0] : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const isEdit = !!selectedItem;

        try {
            let res;

            if (isEdit) {
                const params = new URLSearchParams();
                params.append("group_button", formData.group_button);
                params.append("item_name", formData.item_name);
                params.append("price_tzs", formData.price_tzs);
                params.append("description", formData.description || "");

                res = await fetch(`${MENU_API}?id=${selectedItem.id}&token=${token}`, {
                    method: "PUT",
                    headers: { 
                        "Content-Type": "application/x-www-form-urlencoded",
                        "Authorization": `Bearer ${token}`
                    },
                    body: params.toString()
                });
            } else {
                const payload = new FormData();
                payload.append("group_button", formData.group_button);
                payload.append("item_name", formData.item_name);
                payload.append("price_tzs", formData.price_tzs);
                payload.append("description", formData.description || "");
                if (formData.item_picture) {
                    payload.append("item_picture", formData.item_picture);
                }

                res = await fetch(`${MENU_API}?token=${token}`, { 
                    method: "POST", 
                    headers: { "Authorization": `Bearer ${token}` },
                    body: payload 
                });
            }
            
            if (res.status === 401) {
                Swal.fire("Session Expired", "Please login again", "warning");
                localStorage.removeItem("token");
                localStorage.removeItem("staff");
                window.location.href = "/";
                return;
            }

            const data = await res.json();

            if (data.error) {
                Swal.fire("Error", data.error, "error");
            } else {
                Swal.fire("Success", isEdit ? "Menu updated" : "Menu created", "success");
                setFormData({ group_button: "", item_name: "", price_tzs: "", description: "", item_picture: null });
                setSelectedItem(null);
                setShowModal(false);
                loadMenu();
            }
        } catch (error) {
            console.error("Submit error:", error);
            Swal.fire("Error", "Request failed", "error");
        }
    };

    const editItem = (item) => {
        setSelectedItem(item);
        setFormData({
            group_button: item.group_button,
            item_name: item.item_name,
            price_tzs: item.price_tzs,
            description: item.description || "",
            item_picture: null
        });
        setShowModal(true);
    };

    const deleteItem = async (item) => {
        const confirm = await Swal.fire({
            title: "Delete menu item?",
            text: item.item_name,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33"
        });

        if (!confirm.isConfirmed) return;

        try {
            const res = await fetch(`${MENU_API}?id=${item.id}&token=${token}`, { 
                method: "DELETE",
                headers: { "Authorization": `Bearer ${token}` }
            });
            
            if (res.status === 401) {
                Swal.fire("Session Expired", "Please login again", "warning");
                localStorage.removeItem("token");
                localStorage.removeItem("staff");
                window.location.href = "/";
                return;
            }
            
            const data = await res.json();
            if (data.message) {
                Swal.fire("Deleted!", data.message, "success");
                loadMenu();
            } else if (data.error) {
                Swal.fire("Error", data.error, "error");
            }
        } catch (error) {
            console.error("Delete error:", error);
            Swal.fire("Error", "Failed to delete item", "error");
        }
    };

    const categories = useMemo(() => {
        const groups = [...new Set(menuItems.map(i => i.group_button))];
        return ["all", ...groups];
    }, [menuItems]);

    const filteredItems = useMemo(() => {
        return menuItems.filter(item => {
            const matchSearch = item.item_name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchCategory = categoryFilter === "all" || item.group_button === categoryFilter;
            return matchSearch && matchCategory;
        });
    }, [menuItems, searchTerm, categoryFilter]);

    const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
    const paginatedItems = filteredItems.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    return (
        <div className="pm-menu-container">
            <div className="pm-menu-header">
                <h2 className="pm-menu-title">🍽 Menu Management</h2>
                <button className="pm-btn-primary" onClick={() => {
                    setSelectedItem(null);
                    setFormData({ group_button: "", item_name: "", price_tzs: "", description: "", item_picture: null });
                    setShowModal(true);
                }}>
                    + Add Item
                </button>
            </div>

            <div className="pm-menu-controls">
                <input
                    type="text"
                    placeholder="🔍 Search menu..."
                    className="pm-search-input"
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
                <select
                    className="pm-filter-select"
                    value={categoryFilter}
                    onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
                >
                    {categories.map(cat => (
                        <option key={cat} value={cat}>
                            {cat === "all" ? "All Categories" : cat}
                        </option>
                    ))}
                </select>
            </div>

            <div className="pm-table-wrapper">
                <table className="pm-menu-table">
                    <thead>
                        <tr>
                            <th>Group</th>
                            <th>Name</th>
                            <th>Price</th>
                            <th>Image</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedItems.map(item => (
                            <tr key={item.id}>
                                <td><span className="pm-badge">{item.group_button}</span></td>
                                <td>{item.item_name}</td>
                                <td>TZS {Number(item.price_tzs).toLocaleString()}</td>
                                <td>{item.item_picture && <img src={`${UPLOAD_PATH}${item.item_picture}`} alt="" className="pm-menu-img" />}</td>
                                <td className="pm-action-buttons">
                                    <button onClick={() => editItem(item)} className="pm-btn-edit" title="Edit">✏️</button>
                                    <button onClick={() => deleteItem(item)} className="pm-btn-delete" title="Delete">🗑️</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="pm-pagination">
                <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>← Prev</button>
                <span>Page {currentPage} of {totalPages || 1}</span>
                <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next →</button>
            </div>

            {showModal && (
                <div className="pm-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="pm-modal-box" onClick={e => e.stopPropagation()}>
                        <h3>{selectedItem ? "Edit Item" : "Add Menu Item"}</h3>
                        <form onSubmit={handleSubmit} className="pm-form">
                            <input type="text" name="group_button" placeholder="Category" value={formData.group_button} onChange={handleChange} required />
                            <input type="text" name="item_name" placeholder="Item Name" value={formData.item_name} onChange={handleChange} required />
                            <input type="number" name="price_tzs" placeholder="Price (TZS)" value={formData.price_tzs} onChange={handleChange} required />
                            <textarea name="description" placeholder="Description (optional)" value={formData.description} onChange={handleChange}></textarea>
                            {!selectedItem && <input type="file" name="item_picture" accept="image/*" onChange={handleChange} />}
                            {formData.item_picture && <img src={URL.createObjectURL(formData.item_picture)} alt="" className="pm-preview-img" />}
                            <div className="pm-form-actions">
                                <button type="submit" className="pm-btn-primary">{selectedItem ? "Update" : "Create"}</button>
                                <button type="button" className="pm-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}